import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { DataProvider } from "@/contexts/DataContext";
import { RealtimeProvider } from "@/contexts/RealtimeContext";
import DeadlineListener from "@/components/DeadlineListener";
import { Toaster } from "@/components/ui/sonner";
import AppLayout from "@/components/layout/AppLayout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import AllTasks from "@/pages/AllTasks";
import Whiteboards from "@/pages/Whiteboards";
import WhiteboardEditor from "@/pages/WhiteboardEditor";
import ProjectView from "@/pages/ProjectView";
import SpaceView from "@/pages/SpaceView";
import GlobalCalendar from "@/pages/GlobalCalendar";
import Chat from "@/pages/Chat";
import Files from "@/pages/Files";
import Omzet from "@/pages/Omzet";
import Team from "@/pages/Team";
import { useEffect, useMemo } from "react";

function Protected({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  const fromState = useMemo(() => ({ from: location }), [location]);
  if (user === null) {
    return (
      <div className="h-screen flex items-center justify-center bg-background">
        <div className="h-8 w-8 rounded-full border-2 border-primary border-t-transparent animate-spin" data-testid="app-loading-spinner" />
      </div>
    );
  }
  if (user === false) return <Navigate to="/login" state={fromState} replace />;
  return children;
}

function AdminOnly({ children }) {
  const { user } = useAuth();
  if (user && user.role !== "admin") return <Navigate to="/" replace />;
  return children;
}

function BlockedFor({ roles, children }) {
  const { user } = useAuth();
  if (user && roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function ThemeInit() {
  useEffect(() => {
    const saved = localStorage.getItem("bwn-theme") || "light";
    document.documentElement.classList.toggle("dark", saved === "dark");
  }, []);
  return null;
}

function App() {
  return (
    <div className="App">
      <ThemeInit />
      <AuthProvider>
        <RealtimeProvider>
          <DataProvider>
            <BrowserRouter>
              <DeadlineListener />
              <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/" element={<Protected><AppLayout /></Protected>}>
                <Route index element={<Dashboard />} />
                <Route path="tasks" element={<AllTasks />} />
                <Route path="whiteboards" element={<Whiteboards />} />
                <Route path="whiteboards/:wbId" element={<WhiteboardEditor />} />
                <Route path="files" element={<BlockedFor roles={["pelaksana"]}><Files /></BlockedFor>} />
                <Route path="omzet" element={<AdminOnly><Omzet /></AdminOnly>} />
                <Route path="project/:projectId" element={<ProjectView />} />
                <Route path="space/:spaceId" element={<SpaceView />} />
                <Route path="calendar" element={<GlobalCalendar />} />
                <Route path="chat" element={<Chat />} />
                <Route path="team" element={<Team />} />
              </Route>
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            </BrowserRouter>
          <Toaster position="bottom-right" richColors />
          </DataProvider>
        </RealtimeProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
